import React from 'react';
import { useSnackbar, SnackbarMessage, OptionsObject } from 'notistack';

//
// --- Types ---

export interface StartUpNotificationProps {
  /** time in milliseconds to delay showing the notification */
  delay?: number;
  message: SnackbarMessage;
  snackbarOptions?: OptionsObject;
}

//
// --- StartUpNotification Component ---

/**
 * This component is a bit of a unique case and doesn't render anything. In theory, this
 * logic would be better as a hook, but doing so for this use case would require re-working
 * SupplierSearchApp for this temporary notification. We need to wait for lazy loaded
 * components and api requests to complete before rendering and running this request only
 * one time. It was was easier to pull out into a new component that can trigger the
 * snackbar message when it mounts, rather than tracking state changes in SupplierSearchApp
 */
const StartUpNotification: React.FC<StartUpNotificationProps> = props => {
  const { delay = 0, message, snackbarOptions = {} } = props;
  const hasMessageBeenDisplayedRef = React.useRef(false);

  const { enqueueSnackbar } = useSnackbar();

  React.useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (!hasMessageBeenDisplayedRef.current) {
      const executeEnqueue = () => enqueueSnackbar(message, snackbarOptions);
      hasMessageBeenDisplayedRef.current = true;

      if (delay > 0) {
        timeout = setTimeout(executeEnqueue, delay);
      } else {
        executeEnqueue();
      }
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [delay, enqueueSnackbar, message, snackbarOptions]);

  return null;
};

export default React.memo(StartUpNotification);
